import {createSelector} from 'reselect';

import {isNoneAdvisoryCurrency} from '@stubhub/currency-utils';

/**
 * Checks if the given exchange rate is a valid Number. Valid in this context is
 * - Numeric value
 * - Non NAN value
 * - Finite value
 * - Greater than 0
 * @param {Number} exchangeRate exchange rate between currencies
 * @returns true if it is a finite valid Number
 */
const isValidExchangeRate = (exchangeRate) => {
  return (
    Object.is(typeof exchangeRate, 'number') &&
    !Number.isNaN(exchangeRate) &&
    Number.isFinite(exchangeRate) &&
    exchangeRate > 0
  );
};

/**
 * Returns true if the selected currency is the `NONE` currency
 */
const isNoneCurrencySelector = createSelector(
  ({currencyExchange = {}}) => currencyExchange.selectedCurrency,
  (currency) => {
    return isNoneAdvisoryCurrency(currency);
  }
);

/**
 * Returns true if advisory currency is enabled. For that, all these conditions should be met:
 * - `common.enableAdvisoryCurrency` FF should be enabled
 * - The component has received a currency
 * - There's a selected currency
 * - The initial and selected currency are different
 */
const isAdvisoryCurrencyEnabledSelector = createSelector(
  ({gsConfig = {}}) => {
    return gsConfig.features && gsConfig.features['common.enableAdvisoryCurrency'];
  },
  (_, props) => props.currency,
  ({currencyExchange = {}}) => currencyExchange.selectedCurrency,
  (isFeatureEnabled, currency, selectedCurrency) => {
    const isSameCurrency = selectedCurrency === currency;

    return !!isFeatureEnabled && !!currency && !!selectedCurrency && !isSameCurrency;
  }
);

/**
 * Returns true if advisory currency is enabled based on the conditions explained in `isAdvisoryCurrencyEnabledSelector`
 * @see {@link isAdvisoryCurrencyEnabledSelector}
 */
const shouldApplyAdvisorySelector = createSelector(
  isAdvisoryCurrencyEnabledSelector,
  isNoneCurrencySelector,
  (isEnabled, isNone) => {
    return isEnabled && !isNone;
  }
);

/**
 * Checks if the exchange rate has error. To return `true`, these conditions should be met:
 * - It's not `NONE` currency
 * - There's a exchange rate between currency and selected currency
 * - The exchange rate has no `error` field or set to a falsy value
 */
const hasExchangeRateErrorSelector = createSelector(
  isNoneCurrencySelector,
  (_, props) => props.currency,
  ({currencyExchange = {}}) => currencyExchange.selectedCurrency,
  ({currencyExchange = {}}) => currencyExchange.exchangeRate,
  (isNone, currency, advisoryCurrency, exchangeRate) => {
    const currencyKey = `${currency}_${advisoryCurrency}`;

    return !isNone && exchangeRate && exchangeRate[currencyKey] && exchangeRate[currencyKey].error;
  }
);

/**
 * Returns the currency that should be used. It's based on these conditions
 * - if the exchange has error or the advisory shouldn't be applied, `currency` will be returned
 * - otherwise, selected advisory currency will be returned
 *
 * @see {@link hasExchangeRateErrorSelector} to know when the exchange has error
 * @see {@link shouldApplyAdvisorySelector} for informatin on when should advisory be applied
 */
const currencySelector = createSelector(
  shouldApplyAdvisorySelector,
  (_, props) => props.currency,
  ({currencyExchange = {}}) => currencyExchange.selectedCurrency,
  hasExchangeRateErrorSelector,
  (isAvailable, currency, advisoryCurrency, hasError) => {
    return hasError || !isAvailable ? currency : advisoryCurrency;
  }
);

/**
 * Returns the price with the applied exchange rate if advisory should be applied and there are no errors
 * If there's no exchange rate or it's not valid, it will return null
 *
 * @see {@link hasExchangeRateErrorSelector} to know when the exchange has error
 * @see {@link shouldApplyAdvisorySelector} for information on when should advisory be applied
 * @see {@link isValidExchangeRate} for exchange rate validity check
 */
const priceSelector = createSelector(
  shouldApplyAdvisorySelector,
  (_, props) => props.price,
  (_, props) => props.currency,
  ({currencyExchange = {}}) => currencyExchange.selectedCurrency,
  ({currencyExchange = {}}) => currencyExchange.exchangeRate,
  hasExchangeRateErrorSelector,
  (isAvailable, price, currency, advisoryCurrency, exchangeRate, hasError) => {
    if (!isAvailable || hasError) {
      return price;
    }
    const currencyKey = `${currency}_${advisoryCurrency}`;
    if (
      !exchangeRate ||
      !exchangeRate[currencyKey] ||
      !exchangeRate[currencyKey].amount ||
      !isValidExchangeRate(exchangeRate[currencyKey].amount)
    ) {
      return null;
    }

    return price * exchangeRate[`${currency}_${advisoryCurrency}`].amount;
  }
);

/**
 * Returns if the exchange rate between currency and selected advisory currency is available
 */
const hasBeenLoadedSelector = createSelector(
  (_, props) => props.currency,
  ({currencyExchange = {}}) => currencyExchange.selectedCurrency,
  ({currencyExchange = {}}) => currencyExchange.exchangeRate,
  (currency, advisoryCurrency, exchangeRate) => {
    return exchangeRate && !!exchangeRate[`${currency}_${advisoryCurrency}`];
  }
);

/**
 * Checks if data is still loading.
 * It will be true if `isLoading` prop is true or if advisory should be applied and it hasn't yet been loaded
 *
 * @see {@link shouldApplyAdvisorySelector} for informatin on when should advisory be applied
 * @see {@link hasBeenLoadedSelector} for loaded state
 */
const isLoadingSelector = createSelector(
  shouldApplyAdvisorySelector,
  (_, props) => props.isLoading,
  hasBeenLoadedSelector,
  (shouldBeApplied, isLoading, hasBeenLoaded) => {
    return isLoading || (shouldBeApplied && !hasBeenLoaded);
  }
);

export {
  isLoadingSelector,
  currencySelector,
  hasExchangeRateErrorSelector,
  shouldApplyAdvisorySelector,
  isAdvisoryCurrencyEnabledSelector,
  priceSelector,
};
